import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, OTPInput, Spinner } from '@bravemove/ui-web';
import { CompassWordmark } from '@bravemove/design-tokens/brand';
import { adminFetch } from '../auth/apiClient';
import { type TotpEnrollResponse } from '../auth/types';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors';
import { AuthLayout } from '../layout/AuthLayout';

interface LocationState {
  readonly enrollmentToken?: string;
  /**
   * Deep-link target captured by ProtectedRoute (`state.from`) and threaded
   * through LoginPage; absent on the bootstrap-CLI `?token=` entry path.
   */
  readonly from?: string;
}

export function TotpEnrollPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const auth = useAuth();

  const locationState = (location.state ?? null) as LocationState | null;
  const fromState = locationState?.enrollmentToken;
  const fromQuery = searchParams.get('token');
  const enrollmentToken = fromState ?? fromQuery ?? null;
  const redirectTo = locationState?.from ?? '/';

  const [enrollData, setEnrollData] = useState<TotpEnrollResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!enrollmentToken) {
      navigate('/login', { replace: true });
    }
  }, [enrollmentToken, navigate]);

  useEffect(() => {
    if (!enrollmentToken) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await adminFetch<TotpEnrollResponse>('/admin/auth/totp/enroll', {
          method: 'POST',
          body: { enrollmentToken },
        });
        if (cancelled) return;
        setEnrollData(data);
      } catch (err) {
        if (cancelled) return;
        setEnrollError(translateApiError(err, t));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enrollmentToken, t]);

  if (!enrollmentToken) return null;

  const submit = async (override?: string) => {
    const value = override ?? code;
    if (value.length !== 6) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await auth.confirmEnrollment(enrollmentToken, value);
      // S-15 — honor the deep-link the admin originally requested (threaded
      // through LoginPage as `state.from`); '/' on the bootstrap-CLI path.
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setSubmitError(translateApiError(err, t));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = async () => {
    if (!enrollData?.manualSecret) return;
    try {
      await navigator.clipboard.writeText(enrollData.manualSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard permission — silently ignore.
    }
  };

  return (
    <AuthLayout>
      <header className="text-center mb-6">
        <CompassWordmark size={28} />
        <h1 className="text-h2 font-bold text-ink mt-4">{t('admin:totp.enrollTitle')}</h1>
        <p className="text-body text-ink-2 mt-1">{t('admin:totp.enrollSubtitle')}</p>
      </header>
      {enrollError ? (
        <Banner tone="danger" className="mb-4">
          {enrollError}
        </Banner>
      ) : null}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size={32} />
        </div>
      ) : enrollData ? (
        <>
          <div className="flex justify-center mb-4">
            <img
              src={enrollData.qrDataUrl}
              alt={t('admin:totp.qrAlt')}
              width={200}
              height={200}
              className="rounded-xl border border-hairline bg-white p-2"
            />
          </div>
          <div className="mb-4 p-3 rounded-xl bg-surface-2 border border-hairline">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mb-1">
              {t('admin:totp.manualSecretLabel')}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[13px] font-mono text-ink break-all">
                {enrollData.manualSecret}
              </code>
              <Button type="button" size="xs" variant="secondary" onClick={() => void copySecret()}>
                {copied ? t('admin:common.copied') : t('admin:common.copy')}
              </Button>
            </div>
          </div>
          {submitError ? (
            <Banner tone="danger" className="mb-4">
              {submitError}
            </Banner>
          ) : null}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="space-y-4 flex flex-col items-center"
          >
            <OTPInput
              value={code}
              onChange={setCode}
              onComplete={(value) => void submit(value)}
              autoFocus
              invalid={!!submitError}
              disabled={submitting}
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={submitting}
              disabled={code.length !== 6 || submitting}
            >
              {t('admin:totp.confirmSubmit')}
            </Button>
          </form>
        </>
      ) : null}
    </AuthLayout>
  );
}
